import React, { useState, useMemo } from 'react';
import { Box, Paper, Typography, IconButton, useTheme, Grid } from '@mui/material';
import { Event } from '../../types/Event';
import {
    format,
    startOfWeek,
    addDays,
    startOfMonth,
    endOfMonth,
    endOfWeek,
    isSameMonth,
    isSameDay,
    addMonths,
    subMonths,
    isToday,
    startOfDay
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getDayInfo } from '../../utils/holidays';

interface EventCalendarProps {
    events: Event[];
    onEventClick?: (event: Event) => void;
    onEmptyDayClick?: (date: Date) => void;
}

const EventCalendar: React.FC<EventCalendarProps> = ({ events, onEventClick, onEmptyDayClick }) => {
    const theme = useTheme();
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const { days, monthLabel } = useMemo(() => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(monthStart);
        const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
        const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

        const dateFormat = "d";
        const rows = [];
        let days = [];
        let day = startDate;
        let formattedDate = "";

        const allDays = [];
        while (day <= endDate) {
            const dayEvents = events.filter(e => isSameDay(new Date(e.startDate), day));
            const dayInfo = getDayInfo(day);

            allDays.push({
                date: day,
                formattedDate: format(day, dateFormat),
                isCurrentMonth: isSameMonth(day, monthStart),
                isToday: isToday(day),
                events: dayEvents,
                dayInfo
            });
            day = addDays(day, 1);
        }

        return {
            days: allDays,
            monthLabel: format(monthStart, "MMMM yyyy", { locale: fr })
        };
    }, [currentMonth, events]);

    const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
    const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

    const renderHeader = () => {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, textTransform: 'capitalize' }}>
                    {monthLabel}
                </Typography>
                <Box>
                    <IconButton onClick={handlePrevMonth} sx={{ color: 'text.secondary' }}>
                        <ChevronLeft />
                    </IconButton>
                    <IconButton onClick={handleNextMonth} sx={{ color: 'text.secondary' }}>
                        <ChevronRight />
                    </IconButton>
                </Box>
            </Box>
        );
    };

    const renderDays = () => {
        const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
        return (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 1 }}>
                {weekDays.map((day, i) => (
                    <Typography
                        key={i}
                        variant="caption"
                        align="center"
                        sx={{ color: 'text.secondary', fontWeight: 600, py: 1 }}
                    >
                        {day}
                    </Typography>
                ))}
            </Box>
        );
    };

    const renderCells = () => {
        return (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
                {days.map((dayObj, i) => {
                    const isFutureOrToday = dayObj.date >= startOfDay(new Date());

                    let bgcolor = 'rgba(255, 255, 255, 0.05)'; // Default
                    let color = 'text.primary';
                    let border = '1px solid rgba(255, 255, 255, 0.1)';

                    if (!dayObj.isCurrentMonth) {
                        bgcolor = 'transparent';
                        color = 'text.disabled';
                        border = 'none';
                    } else if (dayObj.events.length > 0) {
                        bgcolor = theme.palette.primary.main;
                        color = '#fff';
                    } else if (dayObj.isToday) {
                        bgcolor = '#FFD700';
                        color = '#000';
                    } else if (dayObj.dayInfo?.type === 'holiday') {
                        bgcolor = 'rgba(244, 67, 54, 0.4)';
                    } else if (dayObj.dayInfo?.type === 'vacation') {
                        bgcolor = 'rgba(76, 175, 80, 0.2)';
                    }

                    if (dayObj.isToday && dayObj.isCurrentMonth) {
                        border = '2px solid #FFD700';
                    }

                    return (
                        <Box
                            key={i}
                            onClick={() => {
                                if (dayObj.events.length > 0 && onEventClick) {
                                    onEventClick(dayObj.events[0]);
                                } else if (onEmptyDayClick && isFutureOrToday) {
                                    onEmptyDayClick(dayObj.date);
                                }
                            }}
                            sx={{
                                minHeight: 80,
                                p: 1,
                                bgcolor,
                                border,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                cursor: (dayObj.events.length > 0 || (onEmptyDayClick && isFutureOrToday)) ? 'pointer' : 'default',
                                position: 'relative',
                                '&:hover': {
                                    bgcolor: dayObj.isCurrentMonth && dayObj.events.length === 0 ? 'rgba(255,255,255,0.08)' : bgcolor
                                }
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: dayObj.isToday ? 'bold' : 'normal',
                                    mb: 0.5,
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                    color: color
                                }}
                            >
                                {dayObj.formattedDate}
                            </Typography>

                            {/* Holiday Name (Optional: if space permits, very small) */}
                            {dayObj.dayInfo?.type === 'holiday' && (
                                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'error.light', textAlign: 'center', lineHeight: 1, mt: 0.5 }}>
                                    {dayObj.dayInfo.name}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 4,
                borderRadius: 4,
                bgcolor: 'transparent', // Let parent control bg or standard
                border: 'none'
            }}
        >
            {renderHeader()}
            {renderDays()}
            {renderCells()}

            {/* Legend */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2, gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: 'rgba(244, 67, 54, 0.4)', mr: 0.5 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Férié</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: 'rgba(76, 175, 80, 0.2)', mr: 0.5 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Vacances</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#FFD700', mr: 0.5, border: `2px solid #FFD700` }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Aujourd'hui</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: theme.palette.primary.main, mr: 0.5 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Événement</Typography>
                </Box>
            </Box>
        </Paper>
    );
};

export default EventCalendar;
